// The shop drawings page's "Deliver approved drawings to: [ ] ACC  [ ] Procore".
// In front of Procore: its connection state, and the shop drawings and models paths
// (owner 2026-10-21), whether or not delivery is ticked.
//
// Ticked, every revision approved from then on is uploaded to that provider's shop
// drawings folder (set from the project card's settings icon), beside the delivery mail
// - utils/externalDelivery on the server. Beside the boxes: what has been sent, and the
// failures with a Retry each.
//
// WHILE A SEND IS ON ITS WAY (owner 2026-10-23): a spinner beside that platform with
// "Uploading 3 of 8 sheets", polled every few seconds until the batch is through.
import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, Popover, Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import addNotification from "helpers/notify";
import { AutodeskLogo, ProcoreLogo } from "./ProviderLogos";
import { getProjectIntegrations, saveProjectIntegration, getDeliveries, retryDelivery } from "./integrationsApi";
import { useProviderStatus } from "./useIntegrations";
import { connectProcore } from "./ProcoreConnectionPanel";
import { ProviderAccounts, destinationConnected } from "./AccountsCount";
import "./integrations.scss";

const PROVIDERS = [
  { key: "acc", name: "ACC", Logo: AutodeskLogo },
  { key: "procore", name: "Procore", Logo: ProcoreLogo },
];

// Fired by the Upload button (pages/ShopDrawings/SendExternalButton) so the bar shows
// the new batch at once rather than at its next poll.
export const SENT_EVENT = "sd-external-sent";

/**
 * The batch one provider is uploading now: every row queued since the oldest one still
 * waiting, so a send of 8 reads "3 of 8" as they land and not "3 of 3 waiting" or
 * "3 of everything ever sent". Null when nothing is waiting.
 */
//
// `destination` narrows it to one destination (rows from before destinations count for
// the provider's first, `isFirst`).
export function uploadProgress(rows, provider, destination, isFirst) {
  const mine = (rows || []).filter((r) => r.provider === provider
    && (!destination || String(r.destination || "") === destination || (!r.destination && isFirst)));
  const waiting = mine.filter((r) => r.status === "queued" || r.status === "running");
  if (!waiting.length) return null;
  const at = (r) => new Date(r.queuedAt || 0).getTime() || 0;
  const since = Math.min(...waiting.map(at));
  const batch = mine.filter((r) => at(r) >= since);
  return { done: batch.filter((r) => r.status === "done").length, total: batch.length };
}

// can this destination upload: its own account's answer when the server names it (an
// older server without it: the viewer's first account, as before)
function uploadsOk(data, key, d) {
  if (d && d.actor && "canUpload" in d.actor) return !!d.actor.canUpload;
  return !!(data[key] && data[key].canUpload);
}

function why(p, name) {
  if (!p) return "";
  if (p.key === "acc" && !p.configured) return `${name} is not set up on this server yet`;
  if (!p.connected) return `Connect your ${name} account in My Projects first`;
  if (p.key === "acc" && !p.canUpload) {
    // the company account this destination uploads as
    const as = p.actor && (p.actor.providerEmail || p.actor.userName);
    return `The Autodesk account this destination uploads as${as ? ` (${as})` : ""} needs connecting again (My Projects) to allow uploads`;
  }
  if (!(p.targets && p.targets.shopDrawings && p.targets.shopDrawings.set)) {
    return `Choose the ${name} shop drawings folder first: the settings icon beside the ${name} logo on the project card`;
  }
  return "";
}

export default function ShopDrawingExternalDelivery({ projectId, canEdit }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState("");
  const procoreStatus = useProviderStatus("procore");

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [d, r] = await Promise.all([getProjectIntegrations(projectId), getDeliveries(projectId, "shopDrawings")]);
      setData(d);
      setRows(r || []);
    } catch (e) {
      setData(null);
    }
  }, [projectId]);

  // every 3 s while something is uploading (the count moves), every 30 s otherwise
  const busy = rows.some((r) => r.status === "queued" || r.status === "running");
  useEffect(() => {
    load();
    const t = setInterval(load, busy ? 3000 : 30000);
    return () => clearInterval(t);
  }, [load, busy]);

  useEffect(() => {
    window.addEventListener(SENT_EVENT, load);
    return () => window.removeEventListener(SENT_EVENT, load);
  }, [load]);

  // nothing until the server has answered for both providers (an older server, or a
  // test double that answers something else, shows no bar rather than a broken one)
  if (!projectId || !data || !data.acc || !data.procore) return null;

  const toggle = async (provider, on, destination) => {
    setSaving(destination || provider);
    try {
      await saveProjectIntegration(projectId, provider, { autoDeliver: { shopDrawings: on } }, destination || undefined);
      await load();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setSaving("");
    }
  };

  const retry = async (id) => {
    try {
      await retryDelivery(projectId, id);
      addNotification("Sent again", "success");
      load();
    } catch (e) {
      addNotification(e.message, "danger");
    }
  };

  // EVERY DESTINATION (owner 2026-10-24: several per provider), each with its own box;
  // a server that lists none gets one entry per provider, as before
  const entries = [];
  for (const { key, name, Logo } of PROVIDERS) {
    const list = (data.destinations || []).filter((d) => d.provider === key);
    // CONNECTED IS THE DESTINATION'S OWN ACCOUNT (owner 2026-10-25), not only my first one
    if (!list.length) entries.push({ key, name, Logo, p: { ...(data[key] || {}), key, connected: destinationConnected(data, key, null), canUpload: uploadsOk(data, key, data[key]) }, destination: "", isFirst: true });
    list.forEach((d, i) => entries.push({
      key, Logo, name: d.name || name, destination: d.destination, isFirst: i === 0,
      // the provider-wide facts, then this destination's own
      p: { ...(data[key] || {}), ...d, configured: data[key].configured, connected: destinationConnected(data, key, d), canUpload: uploadsOk(data, key, d), key },
    }));
  }
  const mineRows = (e) => rows.filter((r) => r.provider === e.key && (!e.destination || String(r.destination || "") === e.destination || (!r.destination && e.isFirst)));

  return (
    <div className="sd-external-delivery">
      <b>Deliver approved drawings to:</b>
      <ProviderAccounts data={data} />
      {entries.map(({ key, name, Logo, p, destination, isFirst }) => {
        const blocked = why(p, name);
        const on = !!(p.autoDeliver && p.autoDeliver.shopDrawings);
        const mine = mineRows({ key, destination, isFirst });
        const failed = mine.filter((r) => r.status === "failed");
        const done = mine.filter((r) => r.status === "done").length;
        const progress = uploadProgress(rows, key, destination, isFirst);
        const tid = destination ? `${key}-${destination}` : key;
        return (
          <span key={destination || key} className="sd-external-provider">
            <Tooltip title={blocked || (on ? `Approved revisions are uploaded to ${p.targets.shopDrawings.path}` : `Upload approved revisions to ${p.targets.shopDrawings.path}`)}>
              <Checkbox checked={on} disabled={!canEdit || saving === (destination || key) || (!!blocked && !on)} onChange={(e) => toggle(key, e.target.checked, destination)}>
                <span className="d-inline-flex align-items-center" style={{ gap: 4 }}>
                  <Logo connected={!!p.connected} size={16} /> {name}
                </span>
              </Checkbox>
            </Tooltip>
            {key === "procore" ? (
              <span className="sd-external-procore">
                {p.connected ? (
                  <span className="text-success" title={procoreStatus && procoreStatus.email ? `as ${procoreStatus.email}` : ""}>
                    Connected
                  </span>
                ) : (
                  <button className="integration-link-btn" style={{ marginLeft: 0 }} onClick={connectProcore} title="Connect your Procore account">
                    Not connected
                  </button>
                )}
                {p.actor ? (
                  <span className="sd-external-path" title="The Procore account this project uploads and downloads with">
                    as {p.actor.providerEmail || p.actor.userName}
                    {!p.actor.connected ? <span className="text-danger"> (not working)</span> : null}
                  </span>
                ) : null}
                <span className="sd-external-path" title="Approved shop drawings go here">
                  Shop drawings: {p.targets.shopDrawings.set ? p.targets.shopDrawings.path : <i>not set</i>}
                </span>
                <span className="sd-external-path" title="Revit models go here">
                  Models: {p.targets.models && p.targets.models.set ? p.targets.models.path : <i>not set</i>}
                </span>
              </span>
            ) : on && p.targets.shopDrawings.set ? (
              <span className="sd-external-path">{p.targets.shopDrawings.path}</span>
            ) : null}
            {done && !progress ? <small className="text-success">{done} sent</small> : null}
            {progress ? (
              <small className="text-primary sd-external-progress" data-testid={`sd-external-progress-${tid}`} title={`Uploading to ${name}`}>
                <Spin size="small" indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} />
                Uploading {progress.done.toLocaleString()} of {progress.total.toLocaleString()} sheet{progress.total === 1 ? "" : "s"}
              </small>
            ) : null}
            {failed.length ? (
              <Popover
                title={`Not delivered to ${name}`}
                content={
                  <div style={{ maxWidth: 420 }}>
                    {failed.slice(0, 20).map((r) => (
                      <div key={r._id} className="mb-2">
                        <div>
                          <b>{r.fileName}</b> (rev {r.revision})
                        </div>
                        <small className="text-danger d-block">{r.error}</small>
                        {canEdit ? (
                          <button className="integration-link-btn" style={{ marginLeft: 0 }} onClick={() => retry(r._id)}>
                            Retry
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                }
              >
                <small className="text-danger" style={{ cursor: "pointer", textDecoration: "underline" }}>
                  {failed.length} failed
                </small>
              </Popover>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}
